import OrderPreviewReconnect from './OrderPreviewReconnect';
import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { useOptionalBrandVerification } from "./BrandRuntimeGate";
import { useOrderStartup } from "./OrderStartupBoundary";
import { storefrontState } from "../lib/orderStartup";

import {brandVerificationFailure} from "../lib/brandVerificationFailure";

// The current customer UIs and install manifests are Counter Culture specific.
// Resolve the backend's brand before mounting either ordering flow.
export default function BrandStorefrontGate({ children }) {
  const orderEntry=/^\/order\/?$/i.test(window.location.pathname);
  const verification = useOptionalBrandVerification();
  const startup = useOrderStartup();
  // Only reuse the response from this fresh startup. Re-entering ordering
  // later, or a long code download, must check current availability again.
  const verified = useMemo(() => startup?.active && verification
    && Date.now() - verification.verifiedAt < 5000 ? verification.profile : null,
  [startup?.active, verification]);
  const report = startup?.report;
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ kind: "loading" });
  const [orderingEntry, setOrderingEntry] = useState(startup?.active);
  if (orderingEntry !== startup?.active) {
    setOrderingEntry(startup?.active);
    setState({ kind: "loading" });
  }

  useEffect(() => {
    // The outer gate already verified this response. Apply the additional
    // storefront brand/live checks without another sequential network call.
    if (verified && !orderEntry) return undefined;
    let current = true;
    setState({ kind: "loading" });
    (verified?Promise.resolve({data:verified}):api.get("/brand/current", { timeout: 8000 }))
      .then(async ({ data }) => {
        let resolved=storefrontState(data);
        if(orderEntry && resolved.kind==='ready'){
          const {data:workspace}=await api.get('/env/public-status',{timeout:8000});
          if(workspace.customer_preview_required)resolved={kind:'preview-unbound'};
        }
        if(current)setState(resolved);
      })
      .catch(error => { if (current) setState({ kind: brandVerificationFailure(error) }); });
    return () => { current = false; };
  }, [attempt, verified, startup?.active]);

  const resolved = verified && !orderEntry ? storefrontState(verified) : state;
  useEffect(() => { report?.("storefront", resolved.kind); }, [report, resolved.kind]);
  if (resolved.kind === "ready") return children;
  const isLoading = resolved.kind === "loading";
  const isExpired = resolved.kind === "preview-expired" || resolved.kind === "preview-unbound";
  const isUnconfigured = resolved.kind === "unconfigured";
  if (isLoading && startup?.visible) return null;
  return (
    <main role="status" aria-live="polite" style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "var(--cc-bg, #F9F5EB)", color: "var(--cc-text, #3C2415)", padding: 24, fontFamily: "Inter, sans-serif" }}>
      <section style={{ width: "min(100%, 460px)", background: "var(--cc-surface, #fff)", border: "1px solid var(--cc-border, #E8DDC8)", borderRadius: 20, padding: 28, boxShadow: "0 12px 40px rgba(60,36,21,.06)" }}>
        <div style={{ fontSize: 12, letterSpacing: ".13em", textTransform: "uppercase", color: "var(--cc-action, #9A3E00)", fontWeight: 700 }}>CulinexOS</div>
        <h1 style={{ fontFamily: "Georgia, serif", fontSize: 28, margin: "14px 0 10px" }}>
          {isLoading ? "Preparing the menu" : isUnconfigured ? resolved.name : isExpired ? (/^\/order\/?$/i.test(window.location.pathname)?"Reconnect CC Order":"Open a fresh Test page") : "Ordering is temporarily unavailable"}
        </h1>
        <p style={{ color: "var(--cc-muted, #6B6B6B)", lineHeight: 1.55, margin: "0 0 20px" }}>
          {isLoading ? "Checking this restaurant's menu…" : isUnconfigured
            ? "Online ordering for this brand is being set up. Please check back soon."
            : isExpired ? (/^\/order\/?$/i.test(window.location.pathname)?"This page needs a valid Test outlet connection. Reconnect to your selected Test workspace below. Your saved orders and account are preserved.":"This Test/Demo link has expired or is no longer valid. In the staff app, select your outlet and Test/Demo mode, then open a fresh PWA test page. Your saved orders and account are preserved.") : "We could not verify this restaurant right now. Please try again shortly."}
        </p>
        {isExpired && (orderEntry?<OrderPreviewReconnect/>:<a href="/" data-testid="open-staff-for-preview" style={{display:"inline-flex",alignItems:"center",minHeight:44,padding:"0 20px",borderRadius:10,background:"var(--cc-action, #126d68)",color:"var(--cc-on-action, white)",textDecoration:"none",fontWeight:600}}>Open staff app</a>)}
        {!isLoading && !isUnconfigured && !isExpired && <button type="button" onClick={() => setAttempt((n) => n + 1)} style={{ minHeight: 44, border: 0, borderRadius: 10, padding: "0 20px", background: "var(--cc-action, #9A3E00)", color: "var(--cc-on-action, white)", fontWeight: 700, cursor: "pointer" }}>Try again</button>}
      </section>
    </main>
  );
}
